import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, TestModeBadge, PageHeader } from "@/components/ui-kit";
import { Loader, EmptyState } from "@/components/Loader";
import { JourneyCard } from "@/components/JourneyCard";
import { Siren, Phone, Users, Check, X, Shield, UserPlus, AlertTriangle } from "lucide-react";

const OFFICIAL = [
  { name: "Police", number: "100" },
  { name: "Ambulance", number: "102 / 108" },
  { name: "Fire", number: "101" },
  { name: "National Emergency", number: "112" },
  { name: "Women Helpline", number: "1091" },
];

export default function Safety() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState(null);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    api.get("/trusted-contacts").then((r) => setContacts(r.data)).catch(() => setContacts([]));
    api.get("/readiness").then((r) => setReadiness(r.data)).catch(() => setReadiness({}));
  }, []);

  if (contacts === null || readiness === null) return <Loader />;

  const checklist = [
    { t: "Profile complete", ok: readiness.profile_complete },
    { t: "Home community confirmed", ok: readiness.home_community_confirmed },
    { t: "Trusted contacts added", ok: readiness.trusted_contacts_added },
    { t: "Emergency permissions tested", ok: readiness.permissions_tested },
  ];

  return (
    <div className="ll-fade-up" data-testid="safety-screen">
      <div className="px-5 pt-8 pb-2 flex items-center justify-between">
        <h1 className="text-3xl font-bold font-head tracking-tight">Safety</h1>
        <TestModeBadge />
      </div>

      <div className="px-5 mt-4">
        <button data-testid="safety-get-help" onClick={() => navigate("/emergency/sos")} className="w-full bg-[#DC2626] text-white rounded-3xl py-8 text-2xl font-black shadow-lg flex flex-col items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Siren className="w-10 h-10" /> Get Help
          <span className="text-sm font-medium opacity-90">Coordinate neighbours & responders</span>
        </button>
      </div>

      <div className="px-5 mt-4">
        <JourneyCard />
      </div>

      <div className="px-5 mt-6">
        <Card>
          <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-bold font-head flex items-center gap-2"><Users className="w-5 h-5 text-[#F97316]" /> Trusted contacts</h3><button data-testid="safety-manage-contacts" onClick={() => navigate("/profile/trusted-contacts")} className="text-sm font-bold text-[#F97316]">Manage</button></div>
          {contacts.length === 0 ? (
            <EmptyState icon={UserPlus} title="No trusted contacts yet" subtitle="Add people who should be alerted in an emergency." action={<button onClick={() => navigate("/profile/trusted-contacts")} data-testid="safety-add-contact" className="bg-[#F97316] text-white rounded-2xl py-3 px-6 font-bold">Add contact</button>} />
          ) : (
            <div className="space-y-2">
              {contacts.slice(0, 4).map((c) => (
                <div key={c.id} className="flex items-center gap-3 py-2">
                  <div className="w-10 h-10 rounded-full bg-[#C4B5FD]/30 flex items-center justify-center font-bold">{c.name[0]}</div>
                  <div className="flex-1"><p className="font-semibold">{c.name} {c.is_primary && <span className="text-xs bg-[#F97316]/15 text-[#F97316] px-2 py-0.5 rounded-full font-bold">Primary</span>}</p><p className="text-sm text-[#111827]/50">{c.relationship} · {c.phone}</p></div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="px-5 mt-4">
        <Card>
          <h3 className="text-lg font-bold font-head flex items-center gap-2 mb-1"><Phone className="w-5 h-5 text-[#F97316]" /> Official emergency contacts</h3>
          <p className="text-xs text-[#92400e] bg-[#F59E0B]/15 rounded-lg px-3 py-2 mb-4 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Sample numbers — verify & replace before launch.</p>
          <div className="space-y-2">
            {OFFICIAL.map((o) => (
              <a key={o.name} href={`tel:${o.number.split(" ")[0]}`} data-testid={`official-${o.name}`} className="flex items-center justify-between py-3 border-b border-[#111827]/5 last:border-0">
                <span className="font-semibold">{o.name}</span>
                <span className="font-bold text-[#111827] bg-[#FFF7E6] px-3 py-1 rounded-lg">{o.number}</span>
              </a>
            ))}
          </div>
        </Card>
      </div>

      <div className="px-5 mt-4">
        <Card>
          <h3 className="text-lg font-bold font-head flex items-center gap-2 mb-4"><Shield className="w-5 h-5 text-[#F97316]" /> Emergency readiness</h3>
          <div className="space-y-3">
            {checklist.map((c, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${c.ok ? "bg-[#16A34A]" : "bg-[#111827]/10"}`}>{c.ok ? <Check className="w-4 h-4 text-white" /> : <X className="w-3.5 h-3.5 text-[#111827]/40" />}</div>
                <span className={c.ok ? "text-[#111827]" : "text-[#111827]/50"}>{c.t}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#111827]/50 mt-4">HumNest does not yet integrate police, ambulance, or volunteer dispatch. Coordination features arrive in Milestone 2.</p>
        </Card>
      </div>
    </div>
  );
}
